import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'

import { Loading } from '../pages/Loading'

interface AuthContextType {
  clientName: string | null
  isLoggedIn: boolean
  isAdmin: boolean
  login: (clientName: string) => void
  logout: () => Promise<void>
  authFetch: (input: string, init?: RequestInit) => Promise<Response>
}

const AUTH_REFRESH_INTERVAL_MS = 30 * 60 * 1000

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

const requestSession = async (): Promise<string | null> => {
  const legacyToken = localStorage.getItem('authToken')
  const response = await fetch('/api/refresh', {
    method: 'POST',
    headers: legacyToken ? { Authorization: legacyToken } : undefined,
  })
  if (response.status >= 500) {
    throw new Error('Failed to refresh session')
  }

  localStorage.removeItem('authToken')
  if (!response.ok) return null

  return (await response.json()).clientName
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const inFlightRefresh = useRef<Promise<string | null>>()
  const { data: clientName, mutate } = useSWR('auth-session', () => {
    inFlightRefresh.current = requestSession()
    return inFlightRefresh.current
  }, {
    refreshInterval: AUTH_REFRESH_INTERVAL_MS,
    refreshWhenHidden: true,
    revalidateOnFocus: false,
  })

  const login = useCallback((newClientName: string) => {
    void mutate(newClientName, { revalidate: false })
  }, [mutate])

  const logout = useCallback(async () => {
    await Promise.allSettled([inFlightRefresh.current])
    await fetch('/api/logout', { method: 'POST' })
    await mutate(null, { revalidate: false })
  }, [mutate])

  const authFetch = useCallback(async (input: string, init?: RequestInit) => {
    const response = await fetch(input, init)
    if (response.status === 401) await logout()
    return response
  }, [logout])

  if (clientName === undefined) return <Loading />

  return (
    <AuthContext.Provider
      value={{
        clientName,
        isLoggedIn: clientName !== null,
        isAdmin: clientName === 'admin',
        login,
        logout,
        authFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useLogOut = () => {
  const { logout } = useAuth()
  const navigate = useNavigate()

  return useCallback(async () => {
    await logout()
    navigate('/login', { replace: true })
  }, [logout, navigate])
}
